import type { Ref } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { TooltipProps } from 'recharts';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    LabelList,
} from 'recharts';
import { useTheme } from '@mui/styles';
import type { ChartReportProps } from 'c-reports/Types';
import { generateBrush } from 'c-reports/Generators';
import { formatCompactLower } from 'c-lib';
import { buildPercentYAxisDomain, buildYAxisDomain } from 'c-reports/yAxisDomain';
import { Box } from '@mui/material';
import PannableSlider from 'c-components/PannableSlider';

type Props = Omit<ChartReportProps<any>, 'onDataUpdated'> & {
    showValueLabels?: boolean;
    valueLabelType?: 'number' | 'percent';
    percentDataKey?: string;
    totalDataKey?: string;
    valuesArePercent?: boolean;
    percentLabelDecimals?: number;
    orientation?: 'vertical' | 'horizontal';
};

const chooseColour = (fallback: string | undefined, override?: string) =>
    typeof override === 'string' && override.trim().length > 0 ? override : fallback ?? '';

const formatSecondsAsHrsMins = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds <= 0) return '0 hrs';
    const totalMinutes = Math.floor(seconds / 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (hrs === 0) return `${mins} mins`;
    if (mins === 0) return `${hrs.toLocaleString()} hrs`;
    return `${hrs.toLocaleString()} hrs ${mins} mins`;
};

const formatPercentLabel = (value: number, decimals = 0): string => {
    if (value === Number.POSITIVE_INFINITY) return '∞%';
    if (!Number.isFinite(value)) return '';
    if (Math.abs(value) >= 1000) return `${formatCompactLower(value)}%`;
    if (decimals > 0) return `${value.toFixed(decimals)}%`;
    if (Math.abs(value) > 0 && Math.abs(value) < 0.5) return `${value.toFixed(2)}%`;
    return `${Math.round(value)}%`;
};

const formatPercentAxisLabel = (value: number): string => {
    if (value === Number.POSITIVE_INFINITY) return '∞%';
    if (!Number.isFinite(value)) return '0%';
    return `${Math.round(value)}%`;
};

const ReportBarChart = (
    {
        data,
        chartProps,
        brushProps,
        parts,
        xAxisDataKey,
        yAxisDataKey,
        renderTooltipLabel,
        yAxisTickFormatter,
        hideLegend,
        hideTooltip,
        hideBrush,
        xAxisProps,
        yAxisProps,
        chartChildren,
        sliderWrapperProps,
        showValueLabels,
        valueLabelType = 'number',
        percentDataKey,
        totalDataKey,
        valuesArePercent = false,
        percentLabelDecimals = 0,
        orientation = 'vertical',
    }: Props,
    ref: Ref<any>,
) => {
    const isHorizontal = orientation === 'horizontal';
    const isTimeMetric = useMemo(
        () => Boolean(totalDataKey && /time/i.test(totalDataKey)),
        [totalDataKey],
    );

    const CustomTooltip = useCallback(
        (tooltipProps: TooltipProps<any, any>) => {
            if (
                renderTooltipLabel &&
                tooltipProps.active &&
                tooltipProps.payload &&
                tooltipProps.payload.length
            ) {
                return renderTooltipLabel(tooltipProps);
            }
            return null;
        },
        [renderTooltipLabel],
    );

    const theme = useTheme();

    const tickFormatter = useCallback(
        (value: any, index: number) => {
            if (valuesArePercent) {
                const n = Number(value);
                return Number.isFinite(n) ? formatPercentAxisLabel(n) : value;
            }
            if (typeof yAxisTickFormatter === 'function') return yAxisTickFormatter(value, index);
            const n = Number(value);
            return Number.isFinite(n) ? formatCompactLower(n) : value;
        },
        [valuesArePercent, yAxisTickFormatter],
    );

    const formatLabel = useCallback(
        (v: any) => {
            const n = Number(v ?? 0);

            if (valueLabelType === 'percent') {
                return formatPercentLabel(n, percentLabelDecimals);
            }

            if (!Number.isFinite(n)) return '';

            if (isTimeMetric) {
                return formatSecondsAsHrsMins(n);
            }

            if (n === 0) return '';

            return formatCompactLower(Math.round(n));
        },
        [valueLabelType, isTimeMetric, percentLabelDecimals],
    );

    const getPercentValueForSeries = useCallback(
        (seriesKey: string, row: Record<string, any>) => {
            const syntheticPercent = Number(row?.[`${seriesKey}__pct`]);
            if (!Number.isNaN(syntheticPercent)) {
                return syntheticPercent;
            }

            const providedPercent = Number(row?.[percentDataKey ?? '']);
            if (!Number.isNaN(providedPercent)) {
                return providedPercent;
            }

            return Number.NaN;
        },
        [percentDataKey],
    );

    const getPercentLabelForSeries = useCallback(
        (seriesKey: string, row: Record<string, any>) => {
            const value = getPercentValueForSeries(seriesKey, row);
            return Number.isNaN(value) ? '' : formatPercentLabel(value, percentLabelDecimals);
        },
        [getPercentValueForSeries, percentLabelDecimals],
    );

    const percentDomain = useMemo(() => {
        if (!valuesArePercent) return undefined;

        const percentValues = data.flatMap(row =>
            parts.map(part => getPercentValueForSeries(String(part.dataKey), row)),
        );
        const finiteValues = percentValues.filter(Number.isFinite);

        if (finiteValues.length === 0) return [0, 100] as [number, number];

        const domain = buildPercentYAxisDomain(finiteValues);
        if (!domain) return undefined;
        return [Math.min(0, ...finiteValues), domain[1]] as [number, number];
    }, [data, getPercentValueForSeries, parts, valuesArePercent]);

    const numericDomain = useMemo(() => {
        if (valuesArePercent) return undefined;

        const numericValues = data.flatMap(row =>
            parts
                .map(part => Number(row?.[String(part.dataKey)]))
                .filter(value => Number.isFinite(value)),
        ) as number[];

        const domain = buildYAxisDomain(numericValues, 'zero');
        if (!domain) return undefined;
        return [Math.min(0, ...numericValues), domain[1]] as [number, number];
    }, [data, parts, valuesArePercent]);

    const effectiveYAxisProps = useMemo(
        () => ({
            ...yAxisProps,
            ...(percentDomain ?? numericDomain ? { domain: percentDomain ?? numericDomain } : {}),
        }),
        [numericDomain, percentDomain, yAxisProps],
    );

    const allBars = useMemo(
        () =>
            parts.map(p => (
                <Bar key={p.dataKey} dataKey={p.dataKey} {...(p.barProps as any)}>
                    {data.map(entry => (
                        <Cell
                            key={`${entry[xAxisDataKey]}-${p.dataKey}`}
                            fill={chooseColour(
                                p.barProps?.fill,
                                (p.barProps?.customColors ?? {})?.[entry[xAxisDataKey]] ??
                                    undefined,
                            )}
                        />
                    ))}
                    {showValueLabels && (
                        <LabelList
                            dataKey={p.dataKey}
                            position="top"
                            content={(labelProps: any) => {
                                const row = data[labelProps.index] ?? labelProps.payload ?? {};
                                const text =
                                    valueLabelType === 'percent'
                                        ? getPercentLabelForSeries(p.dataKey, row)
                                        : formatLabel(labelProps.value);

                                if (!text) return null;

                                return (
                                    <text
                                        x={labelProps.x + labelProps.width / 2}
                                        y={labelProps.y}
                                        dy={-4}
                                        textAnchor="middle"
                                        fill={p.barProps?.fill ?? undefined}
                                        fontSize={12}
                                    >
                                        {text}
                                    </text>
                                );
                            }}
                        />
                    )}
                </Bar>
            )),
        [
            parts,
            data,
            xAxisDataKey,
            showValueLabels,
            valueLabelType,
            formatLabel,
            getPercentLabelForSeries,
        ],
    );

    const maxVisibleLabels = 15;
    const maxCharsPerLabel = 10;

    const adjustedEndIndex = useMemo(() => {
        const avgLabelLength =
            data.reduce((sum, item) => sum + String(item[xAxisDataKey] ?? '').length, 0) /
            Math.max(1, data.length);

        if (avgLabelLength <= maxCharsPerLabel && data.length <= 24) {
            return data.length - 1;
        }
        return Math.min(Math.max(0, data.length - 1), maxVisibleLabels);
    }, [data, xAxisDataKey]);

    const [brushRange, setBrushRange] = useState<[number, number]>([0, adjustedEndIndex]);

    const xAxisTickFontSize = useMemo(() => {
        const tickProp = xAxisProps?.tick;
        if (tickProp && typeof tickProp === 'object' && 'fontSize' in tickProp) {
            return Number((tickProp as { fontSize?: number }).fontSize) || 14;
        }
        return 14;
    }, [xAxisProps?.tick]);

    const xAxisTickLineHeight = useMemo(
        () => Math.max(12, Math.round(xAxisTickFontSize * 1.15)),
        [xAxisTickFontSize],
    );

    useEffect(() => {
        setBrushRange([0, adjustedEndIndex]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.length]);

    if (isHorizontal) {
        const numberDomain = percentDomain ?? numericDomain;
        return (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    ref={ref}
                    data={data}
                    layout="vertical"
                    margin={{ top: 16, right: 64, left: 20, bottom: 5 }}
                    {...chartProps}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        type="number"
                        tickFormatter={tickFormatter}
                        {...(numberDomain ? { domain: numberDomain } : {})}
                    />
                    <YAxis
                        type="category"
                        dataKey={xAxisDataKey}
                        width={140}
                        tick={{ fontSize: 13, fill: '#666' }}
                        tickFormatter={(value: any) => {
                            const s = String(value ?? '');
                            return s.length > 18 ? `${s.slice(0, 17)}…` : s;
                        }}
                        {...yAxisProps}
                    />
                    {!hideTooltip && (
                        <Tooltip content={renderTooltipLabel ? <CustomTooltip /> : undefined} />
                    )}
                    {!hideLegend && (
                        <Legend verticalAlign="top" align="right" wrapperStyle={{ top: 5 }} />
                    )}
                    {parts.map(p => (
                        <Bar key={p.dataKey} dataKey={p.dataKey} {...(p.barProps as any)}>
                            {data.map(entry => (
                                <Cell
                                    key={`${entry[xAxisDataKey]}-${p.dataKey}`}
                                    fill={chooseColour(
                                        p.barProps?.fill,
                                        (p.barProps?.customColors ?? {})?.[entry[xAxisDataKey]] ??
                                            undefined,
                                    )}
                                />
                            ))}
                            {showValueLabels && (
                                <LabelList
                                    dataKey={p.dataKey}
                                    position="right"
                                    content={(labelProps: any) => {
                                        const row =
                                            data[labelProps.index] ?? labelProps.payload ?? {};
                                        const text =
                                            valueLabelType === 'percent'
                                                ? getPercentLabelForSeries(p.dataKey, row)
                                                : formatLabel(labelProps.value);

                                        if (!text) return null;

                                        return (
                                            <text
                                                x={labelProps.x + labelProps.width + 4}
                                                y={labelProps.y + labelProps.height / 2}
                                                dominantBaseline="central"
                                                textAnchor="start"
                                                fill={p.barProps?.fill ?? undefined}
                                                fontSize={12}
                                            >
                                                {text}
                                            </text>
                                        );
                                    }}
                                />
                            )}
                        </Bar>
                    ))}
                    {[...(chartChildren ?? [])]}
                </BarChart>
            </ResponsiveContainer>
        );
    }

    return (
        <>
            <ResponsiveContainer width="100%" height={data.length > 1 ? '94%' : '100%'}>
                <BarChart
                    ref={ref}
                    data={data}
                    margin={{ top: 25, right: 50, left: 40, bottom: 5 }}
                    {...chartProps}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey={xAxisDataKey}
                        interval={0}
                        tickMargin={10}
                        {...xAxisProps}
                        tick={props => {
                            const { x, y, payload } = props as any;
                            const words = String(payload?.value ?? '').split(' ');
                            const maxCharsPerLine = 10;

                            const lines: string[] = [];
                            let current = '';
                            words.forEach(w => {
                                if ((current + w).length > maxCharsPerLine) {
                                    lines.push(current);
                                    current = w;
                                } else {
                                    current += (current ? ' ' : '') + w;
                                }
                            });
                            if (current) lines.push(current);

                            return (
                                <text
                                    x={x}
                                    y={y}
                                    textAnchor="middle"
                                    fontSize={xAxisTickFontSize}
                                    fill="#666"
                                >
                                    {lines.map((line, idx) => (
                                        <tspan
                                            key={`${String(payload?.value ?? '')}-${line}`}
                                            x={x}
                                            dy={idx === 0 ? 0 : xAxisTickLineHeight}
                                        >
                                            {line}
                                        </tspan>
                                    ))}
                                </text>
                            );
                        }}
                    />
                    <YAxis
                        width={20}
                        dataKey={yAxisDataKey}
                        tickFormatter={tickFormatter}
                        {...effectiveYAxisProps}
                    />
                    {!hideTooltip && (
                        <Tooltip content={renderTooltipLabel ? <CustomTooltip /> : undefined} />
                    )}
                    {!hideLegend && (
                        <Legend verticalAlign="top" align="right" wrapperStyle={{ top: 5 }} />
                    )}

                    {allBars}

                    {!hideBrush &&
                        generateBrush({
                            brushProps: { display: 'hidden', ...brushProps },
                            startIndex: Math.floor(brushRange[0]),
                            endIndex: Math.ceil(brushRange[1]),
                            dataKey: xAxisDataKey,
                            theme,
                            height: 0,
                        })}

                    {[...(chartChildren ?? [])]}
                </BarChart>
            </ResponsiveContainer>

            {!hideBrush && data.length > 1 && (
                <Box pl={9} width="95%" mt={-1} {...sliderWrapperProps}>
                    <PannableSlider
                        value={brushRange}
                        onChange={setBrushRange}
                        min={0}
                        max={Math.max(0, data.length - 1)}
                        getLabelForValue={i => data[i]?.[xAxisDataKey] ?? i}
                    />
                </Box>
            )}
        </>
    );
};

export default React.forwardRef(ReportBarChart);
